import React, { useEffect, useState } from "react";
import { Stack, Text, TextInput, Title } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconCircleCheck, IconExclamationMark } from "@tabler/icons";
import { useTranslation } from "react-i18next";
import { Page, FormSectionCard } from "../../layout/page";
import PageListHeader from "../../components/pageListHeader";
import { useServerApi } from "../../hooks/userServerApi";
import { useTabNavigate } from "../../hooks/useTabNavigate";

const OcrAiSetting = () => {
  const [api] = useServerApi();
  const { t } = useTranslation();
  const tabNav = useTabNavigate();
  const [baseUrl, setBaseUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [apiKeyConfigured, setApiKeyConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const applySettings = (data) => {
    if (!data) return;
    setBaseUrl(data.baseUrl || "");
    setApiKeyConfigured(data.apiKeyConfigured === true);
  };

  useEffect(() => {
    let active = true;
    const fetchSetting = async () => {
      try {
        setLoading(true);
        const res = await api.ocrAiSettings.get();
        if (active && res?.success && res.data) applySettings(res.data);
      } catch (error) {
        console.log(error);
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchSetting();
    return () => {
      active = false;
    };
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = { baseUrl };
      if (apiKey.trim()) payload.apiKey = apiKey.trim();
      const res = await api.ocrAiSettings.save(payload);
      if (res?.success) {
        setApiKey("");
        applySettings(res.data);
        showNotification({
          title: t("Saved"),
          message: t("MBID AI OCR settings saved."),
          color: "green",
          icon: <IconCircleCheck />,
        });
        return;
      }
      showNotification({
        title: t("Error"),
        message:
          res?.error?.msg || res?.message || t("Could not save MBID AI OCR settings."),
        color: "red",
        icon: <IconExclamationMark />,
      });
    } catch (error) {
      showNotification({
        title: t("Error"),
        message: error.message,
        color: "red",
        icon: <IconExclamationMark />,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      const res = await api.ocrAiSettings.testConnection({ baseUrl });
      if (res?.success) {
        showNotification({
          title: t("MBID AI OCR is reachable"),
          message: t("The health check succeeded."),
          color: "green",
          icon: <IconCircleCheck />,
        });
        return;
      }
      showNotification({
        title: t("Test failed"),
        message:
          res?.error?.msg ||
          res?.msg ||
          res?.message ||
          t("Could not reach MBID AI OCR."),
        color: "red",
        icon: <IconExclamationMark />,
      });
    } catch (error) {
      showNotification({
        title: t("Test failed"),
        message: error.message,
        color: "red",
        icon: <IconExclamationMark />,
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Page>
      <PageListHeader
        title={t("MBID AI OCR")}
        actionButtons={[
          {
            label: t("Test connection"),
            onClick: handleTest,
            loading: testing,
            disabled: loading,
            variant: "default",
          },
          {
            label: t("Save"),
            onClick: handleSave,
            loading: saving,
            disabled: loading,
          },
          {
            label: t("Close"),
            onClick: () => tabNav.closeActive(),
            variant: "default",
          },
        ]}
      />
      {!loading && (
        <Stack spacing="md">
          <FormSectionCard>
            <Title order={5}>{t("MBID AI OCR server")}</Title>
            <Text size="xs" color="dimmed" mb="md">
              {t(
                "Save the MBID AI OCR base URL and API key. AI workflows keep using the current OCR provider until that step is switched over."
              )}
            </Text>
            <TextInput
              label={t("Base URL")}
              description={t("Example: http://127.0.0.1:3000")}
              placeholder="http://127.0.0.1:3000"
              value={baseUrl}
              onChange={(event) => setBaseUrl(event.currentTarget.value)}
              mb="md"
            />
            <TextInput
              label={t("API key")}
              type="password"
              placeholder={
                apiKeyConfigured
                  ? t("Leave blank to keep the current key")
                  : t("ocrai_...")
              }
              value={apiKey}
              onChange={(event) => setApiKey(event.currentTarget.value)}
            />
            {apiKeyConfigured ? (
              <Text size="xs" color="dimmed" mt="xs">
                {t("An API key is already stored.")}
              </Text>
            ) : null}
          </FormSectionCard>
        </Stack>
      )}
    </Page>
  );
};

export default OcrAiSetting;
